import { useEffect, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import ToolsLayout from '@/components/layout/ToolsLayout'
import { PageHeader, PageShell } from '@/components/shared/layout'
import { useQuery } from '@tanstack/react-query'
import { useTheme } from '@/lib/contexts/ThemeContext'
import '@/styles/iso.css'

// Each card shows the tool as a small bench instrument (anatomy kit, built by
// `npm run figures:build` into public/tool-figures/). Static, same-origin markup, never user input.
type ToolFigureKey = 'utm' | 'planner' | 'significance' | 'conversion'
const FIGURE_FOR: Record<string, ToolFigureKey> = {
  'utm-generator': 'utm',
  'test-planner': 'planner',
  'significance-calculator': 'significance',
  'conversion-rate-calculator': 'conversion',
}

function ToolFigure({ name, theme }: { name: ToolFigureKey; theme: string }) {
  const { data } = useQuery({
    queryKey: ['tool-figure', name],
    queryFn: async () => {
      const res = await fetch(`/tool-figures/${name}.svg`)
      if (!res.ok) throw new Error(`Tool figure ${name} failed to load`)
      return res.text()
    },
    staleTime: Infinity,
  })
  return (
    <div
      className="iso flex aspect-[13/6] items-center justify-center overflow-hidden border-b border-border bg-[var(--anatomy-paper)] px-2"
      data-theme={theme}
      dangerouslySetInnerHTML={data ? { __html: data } : undefined}
    />
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Tools() {
  const params = useParams()
  const basePath = params.workspaceId
    ? `/workspace/${params.workspaceId}`
    : params.propertySlugId
      ? `/p/${params.propertySlugId}`
      : ''

  useEffect(() => {
    document.title = 'Marketing tools - Humblytics'
  }, [])

  const Wrapper = basePath ? ({ children }: { children: ReactNode }) => <>{children}</> : ToolsLayout
  return (
    <Wrapper>
      <ToolsIndex basePath={basePath} />
    </Wrapper>
  )
}

export function ToolsIndex({ basePath }: { basePath: string }) {
  const navigate = useNavigate()
  const { theme } = useTheme()

  const tools = [
    {
      id: 'utm-generator',
      category: 'Campaign tracking',
      title: 'UTM generator',
      description: 'Create trackable campaign links with UTM parameters for accurate attribution.',
      href: `${basePath}/tools/utm-generator`,
    },
    {
      id: 'test-planner',
      category: 'Testing and analytics',
      title: 'A/B test planner',
      description: 'Plan tests with sample size, duration estimates and goal-based guidance.',
      href: `${basePath}/test-planner`,
    },
    {
      id: 'significance-calculator',
      category: 'Testing and analytics',
      title: 'Significance calculator',
      description: 'Check whether your A/B test results are statistically significant.',
      href: `${basePath}/tools/significance-calculator`,
    },
    {
      id: 'conversion-rate-calculator',
      category: 'Testing and analytics',
      title: 'Conversion rate calculator',
      description: 'Calculate conversion rate and compare it against industry benchmarks.',
      href: `${basePath}/tools/conversion-rate-calculator`,
    },
  ]

  return (
      <PageShell>
        <PageHeader
          title="Marketing tools"
          description="Calculators and link builders for planning campaigns and tests."
        />

        <div className="well grid grid-cols-1 gap-2 md:grid-cols-2">
          {tools.map((tool) => {
            return (
              <article
                key={tool.id}
                role="link"
                tabIndex={0}
                onClick={() => navigate(tool.href)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(tool.href)
                }}
                className="surface-raised group flex cursor-pointer flex-col overflow-clip transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <ToolFigure name={FIGURE_FOR[tool.id]} theme={theme} />
                <div className="flex flex-1 flex-col gap-4 px-[18px] pb-[18px] pt-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs leading-4 text-foreground/60">{tool.category}</span>
                    <h2 className="text-sm font-semibold leading-[18px]">{tool.title}</h2>
                    <p className="text-[13px] leading-5 text-foreground/60">{tool.description}</p>
                  </div>
                  <Button
                    variant="outline"
                    className="h-[34px] w-fit rounded-lg border-input bg-background px-3 text-xs font-medium shadow-none"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(tool.href)
                    }}
                  >
                    Launch
                  </Button>
                </div>
              </article>
            )
          })}
        </div>
      </PageShell>
  )
}
